import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, ChevronDown, ChevronUp, ClipboardList, Download, Bell, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getPracticeTemplates,
  getPracticeTemplate,
  createPracticeTemplate,
  updatePracticeTemplate,
  deletePracticeTemplate,
  addTemplateItem,
  updateTemplateItem,
  deleteTemplateItem,
  getStandardTemplates,
  importStandardTemplate,
  getReminderTriggers,
  createReminderTrigger,
  updateReminderTrigger,
  deleteReminderTrigger
} from '../../utils/practiceImmunizationTemplateApi';
import { getImmunizationCatalog } from '../../utils/immunizationCatalogApi';
import {
  PracticeTemplateListItem,
  PracticeTemplate,
  PracticeTemplateItem,
  CreatePracticeTemplate,
  CreatePracticeTemplateItem,
  StandardTemplate,
  ReminderTrigger,
  DeliveryMethod
} from '../../types/practice-immunization-template';
import { ImmunizationCatalogItem } from '../../types/immunization-catalog';
import ConfirmDialog from '../common/ConfirmDialog';

// ===== HELPER FUNCTIONS =====

function minutesToDisplayString(minutes: number): string {
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  
  const parts = [];
  if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
  if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
  if (mins > 0) parts.push(`${mins} minute${mins !== 1 ? 's' : ''}`);
  
  return parts.join(' ') || '0 minutes';
}

// ===== REMINDER TRIGGERS MODAL =====

interface ReminderTriggersModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: PracticeTemplateListItem | null;
}

const ReminderTriggersModal: React.FC<ReminderTriggersModalProps> = ({
  isOpen, onClose, template
}) => {
  const [triggers, setTriggers] = useState<ReminderTrigger[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form state for new/edit trigger
  const [editingTrigger, setEditingTrigger] = useState<ReminderTrigger | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formDays, setFormDays] = useState(3);
  const [formHours, setFormHours] = useState(0);
  const [formMinutes, setFormMinutes] = useState(0);
  const [formDeliveryMethod, setFormDeliveryMethod] = useState<DeliveryMethod>('EMAIL');
  const [formEnabled, setFormEnabled] = useState(true);

  useEffect(() => {
    if (isOpen && template) {
      fetchTriggers();
    }
  }, [isOpen, template]);

  const fetchTriggers = async () => {
    if (!template) return;
    
    setLoading(true);
    setError(null);
    try {
      const data = await getReminderTriggers(template.id);
      setTriggers(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load triggers');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingTrigger(null);
    setShowForm(false);
    setFormDays(3);
    setFormHours(0);
    setFormMinutes(0);
    setFormDeliveryMethod('EMAIL');
    setFormEnabled(true);
  };

  const openAddForm = () => {
    resetForm();
    setShowForm(true);
  };

  const openEditForm = (trigger: ReminderTrigger) => {
    const totalMinutes = trigger.minutes_before;
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const mins = totalMinutes % 60;
    
    setEditingTrigger(trigger);
    setFormDays(days);
    setFormHours(hours);
    setFormMinutes(mins);
    setFormDeliveryMethod(trigger.delivery_method);
    setFormEnabled(trigger.is_enabled);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!template) return;
    
    const totalMinutes = (formDays * 1440) + (formHours * 60) + formMinutes;
    if (totalMinutes <= 0) {
      setError('Please set a reminder time');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (editingTrigger) {
        await updateReminderTrigger(template.id, editingTrigger.id, {
          minutes_before: totalMinutes,
          delivery_method: formDeliveryMethod,
          is_enabled: formEnabled
        });
      } else {
        await createReminderTrigger(template.id, {
          minutes_before: totalMinutes,
          delivery_method: formDeliveryMethod,
          is_enabled: formEnabled
        });
      }
      await fetchTriggers();
      resetForm();
    } catch (err: any) {
      setError(err.message || 'Failed to save trigger');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (trigger: ReminderTrigger) => {
    if (!template) return;
    if (!window.confirm('Delete this reminder trigger?')) return;

    try {
      await deleteReminderTrigger(template.id, trigger.id);
      await fetchTriggers();
    } catch (err: any) {
      setError(err.message || 'Failed to delete trigger');
    }
  };

  const handleToggleEnabled = async (trigger: ReminderTrigger) => {
    if (!template) return;

    try {
      await updateReminderTrigger(template.id, trigger.id, {
        is_enabled: !trigger.is_enabled
      });
      await fetchTriggers();
    } catch (err: any) {
      setError(err.message || 'Failed to update trigger');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Reminder Settings</h2>
            <p className="text-sm text-gray-500">{template?.name}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-md">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : (
            <>
              {/* Existing triggers */}
              {triggers.length > 0 && !showForm && (
                <div className="space-y-3 mb-4">
                  {triggers.map((trigger) => (
                    <div
                      key={trigger.id}
                      className={`p-4 rounded-lg border ${trigger.is_enabled ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Bell className={`w-5 h-5 ${trigger.is_enabled ? 'text-blue-600' : 'text-gray-400'}`} />
                          <div>
                            <div className="font-medium text-gray-900">
                              {minutesToDisplayString(trigger.minutes_before)} before
                            </div>
                            <div className="text-sm text-gray-500">
                              {trigger.delivery_method === 'EMAIL_HTML' ? 'HTML Email' : trigger.delivery_method}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleToggleEnabled(trigger)}
                            className={`px-3 py-1 rounded text-sm font-medium ${
                              trigger.is_enabled 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            {trigger.is_enabled ? 'On' : 'Off'}
                          </button>
                          <button
                            onClick={() => openEditForm(trigger)}
                            className="p-1 text-gray-400 hover:text-blue-600"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(trigger)}
                            className="p-1 text-gray-400 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add/Edit form */}
              {showForm ? (
                <div className="border rounded-lg p-4 bg-gray-50">
                  <h3 className="font-medium text-gray-900 mb-4">
                    {editingTrigger ? 'Edit Reminder' : 'Add Reminder'}
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Remind before vaccine is due:
                      </label>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Days</label>
                          <input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={formDays === 0 ? '' : formDays}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === '' || /^\d+$/.test(val)) {
                                setFormDays(val === '' ? 0 : parseInt(val));
                              }
                            }}
                            placeholder="0"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Hours</label>
                          <input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={formHours === 0 ? '' : formHours}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === '' || /^\d+$/.test(val)) {
                                const num = val === '' ? 0 : Math.min(23, parseInt(val));
                                setFormHours(num);
                              }
                            }}
                            placeholder="0"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Minutes</label>
                          <input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={formMinutes === 0 ? '' : formMinutes}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === '' || /^\d+$/.test(val)) {
                                const num = val === '' ? 0 : Math.min(59, parseInt(val));
                                setFormMinutes(num);
                              }
                            }}
                            placeholder="0"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Delivery method:
                      </label>
                      <select
                        value={formDeliveryMethod}
                        onChange={(e) => setFormDeliveryMethod(e.target.value as DeliveryMethod)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        <option value="EMAIL">Email</option>
                        <option value="EMAIL_HTML">HTML Email</option>
                        <option value="SMS">SMS</option>
                      </select>
                    </div>

                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formEnabled}
                        onChange={(e) => setFormEnabled(e.target.checked)}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="text-sm text-gray-700">Enable this reminder</span>
                    </label>
                  </div>

                  <div className="flex justify-end gap-2 mt-4">
                    <button
                      onClick={resetForm}
                      className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={openAddForm}
                  className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Add Reminder Trigger
                </button>
              )}

              {/* Help text */}
              {triggers.length === 0 && !showForm && (
                <p className="text-sm text-gray-500 mt-4 text-center">
                  Add reminder triggers to automatically notify pet owners before vaccines are due.
                  Reminders will be created for each vaccine when this template is applied to a pet.
                </p>
              )}
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ===== IMPORT STANDARD TEMPLATES MODAL =====

interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (templateId: string, customName?: string) => Promise<void>;
  standardTemplates: StandardTemplate[];
}

const ImportTemplateModal: React.FC<ImportModalProps> = ({
  isOpen, onClose, onImport, standardTemplates
}) => {
  const [selectedId, setSelectedId] = useState<string>('');
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedId('');
    }
  }, [isOpen]);

  const handleImport = async () => {
    if (!selectedId) return;

    setImporting(true);
    try {
      await onImport(selectedId, undefined);
      onClose();
    } catch (err: any) {
      alert('Failed to import template: ' + err.message);
    } finally {
      setImporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Import Template</h2>
        </div>
        
        <div className="p-4 max-h-80 overflow-y-auto">
          {standardTemplates.length === 0 ? (
            <p className="text-center text-gray-500 py-4">No templates available</p>
          ) : (
            <div className="space-y-1">
              {standardTemplates.map((template) => (
                <label
                  key={template.id}
                  className={`flex items-center gap-3 p-3 rounded-md cursor-pointer ${
                    selectedId === template.id ? 'bg-blue-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="template"
                    checked={selectedId === template.id}
                    onChange={() => setSelectedId(template.id)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900">{template.name}</div>
                    <div className="text-sm text-gray-500">
                      {template.species} · {template.items.length} vaccines
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={importing || !selectedId}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {importing ? 'Importing...' : 'Import'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ===== CREATE/EDIT TEMPLATE MODAL =====

interface TemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreatePracticeTemplate) => Promise<void>;
  template?: PracticeTemplate;
  catalogItems: ImmunizationCatalogItem[];
}

const TemplateModal: React.FC<TemplateModalProps> = ({
  isOpen, onClose, onSave, template, catalogItems
}) => {
  const [name, setName] = useState('');
  const [species, setSpecies] = useState('');
  const [description, setDescription] = useState('');
  const [items, setItems] = useState<CreatePracticeTemplateItem[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (template) {
      setName(template.name);
      setSpecies(template.species || '');
      setDescription(template.description || '');
      setItems(template.items.map(i => ({
        immunization_name: i.immunization_name,
        offset_days: i.offset_days,
        age_weeks: i.age_weeks,
        age_days: i.age_days,
        catalog_item_id: i.catalog_item_id,
        series_number: i.series_number,
        notes: i.notes,
        display_order: i.display_order
      })));
    } else {
      setName('');
      setSpecies('');
      setDescription('');
      setItems([]);
    }
  }, [template, isOpen]);

  const handleAddItem = () => {
    setItems([...items, {
      immunization_name: '',
      age_weeks: undefined,  // Default to age-based scheduling
      offset_days: undefined,
      catalog_item_id: '',  // Required - must select from catalog
      series_number: undefined,
      notes: undefined,
      display_order: items.length
    }]);
  };

  const handleItemChange = (index: number, field: string, value: any) => {
    const newItems = [...items];
    if (field === 'catalog_item_id' && value) {
      // When catalog item is selected, also set the immunization_name
      const catalogItem = catalogItems.find(c => c.id === value);
      if (catalogItem) {
        newItems[index] = { ...newItems[index], catalog_item_id: value, immunization_name: catalogItem.name };
      }
    } else {
      newItems[index] = { ...newItems[index], [field]: value };
    }
    setItems(newItems);
  };

  const handleRemoveItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        species: species || undefined,
        description: description || undefined,
        items: items.filter(i => i.immunization_name)
      });
      onClose();
    } catch (err: any) {
      alert('Failed to save template: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {template ? 'Edit Template' : 'Create Vaccination Protocol'}
          </h2>
        </div>
        
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Template Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Puppy Core Series"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Species
              </label>
              <select
                value={species}
                onChange={(e) => setSpecies(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Species</option>
                <option value="Dog">Dog</option>
                <option value="Cat">Cat</option>
                <option value="Horse">Horse</option>
                <option value="Bird">Bird</option>
                <option value="Rabbit">Rabbit</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe this vaccination protocol..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Template Items */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">
                Vaccinations in Protocol
              </label>
              <button
                type="button"
                onClick={handleAddItem}
                className="inline-flex items-center gap-1 px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Vaccine
              </button>
            </div>
            
            {items.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                <ClipboardList className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">No vaccines added yet</p>
                <button
                  type="button"
                  onClick={handleAddItem}
                  className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  Add your first vaccine
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {/* Header row */}
                <div className="grid grid-cols-12 gap-3 px-3 text-xs text-gray-500 font-medium">
                  <div className="col-span-5">Vaccine</div>
                  <div className="col-span-3">Age (weeks)</div>
                  <div className="col-span-2">Dose #</div>
                  <div className="col-span-2"></div>
                </div>
                {items.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1 grid grid-cols-12 gap-3 items-center">
                      {/* Vaccine Name - 5 cols (REQUIRED: must select from catalog) */}
                      <div className="col-span-5">
                        <select
                          value={item.catalog_item_id || ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val) {
                              handleItemChange(index, 'catalog_item_id', val);
                            }
                          }}
                          className={`w-full px-2 py-1.5 text-sm border rounded-md ${
                            !item.catalog_item_id ? 'border-red-300 bg-red-50' : 'border-gray-300'
                          }`}
                          required
                        >
                          <option value="">-- Select vaccine --</option>
                          {catalogItems.filter(c => c.is_active).map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                      {/* Age - 3 cols */}
                      <div className="col-span-3">
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={item.age_weeks ?? item.offset_days ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            // Allow empty or numeric only
                            if (val === '' || /^\d+$/.test(val)) {
                              handleItemChange(index, 'age_weeks', val === '' ? undefined : parseInt(val));
                            }
                          }}
                          placeholder="Age (weeks)"
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                        />
                      </div>
                      {/* Dose # - 2 cols */}
                      <div className="col-span-2">
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={item.series_number ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '' || /^\d+$/.test(val)) {
                              handleItemChange(index, 'series_number', val === '' ? undefined : parseInt(val));
                            }
                          }}
                          placeholder="Dose #"
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                        />
                      </div>
                      {/* Delete - 2 cols */}
                      <div className="col-span-2 flex justify-end">
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </form>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !name.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : template ? 'Update Template' : 'Create Template'}
          </button>
        </div>
      </div>
    </div>
  );
};

const PracticeTemplatesManagement: React.FC = () => {
  const { user } = useAuth();
  const [templates, setTemplates] = useState<PracticeTemplateListItem[]>([]);
  const [catalogItems, setCatalogItems] = useState<ImmunizationCatalogItem[]>([]);
  const [standardTemplates, setStandardTemplates] = useState<StandardTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderTemplate, setReminderTemplate] = useState<PracticeTemplateListItem | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<PracticeTemplate | undefined>();
  const [expandedTemplate, setExpandedTemplate] = useState<string | null>(null);
  const [expandedDetails, setExpandedDetails] = useState<PracticeTemplate | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<PracticeTemplateListItem | null>(null);

  useEffect(() => {
    if (user?.practice_id) {
      fetchData();
    }
  }, [user?.practice_id]);

  const fetchData = async () => {
    if (!user?.practice_id) return;

    setLoading(true);
    try {
      const [templatesData, catalogData, standardData] = await Promise.all([
        getPracticeTemplates(user.practice_id, { activeOnly: false }),
        getImmunizationCatalog(user.practice_id, undefined, true),
        getStandardTemplates()
      ]);
      setTemplates(templatesData);
      setCatalogItems(catalogData);
      setStandardTemplates(standardData);
    } catch (err: any) {
      console.error('Failed to fetch data:', err);
      alert('Failed to load templates: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedTemplate(undefined);
    setShowModal(true);
  };

  const handleImport = async (standardTemplateId: string, customName?: string) => {
    if (!user?.practice_id) return;

    await importStandardTemplate(user.practice_id, {
      standard_template_id: standardTemplateId,
      custom_name: customName
    });
    await fetchData();
  };

  const handleEdit = async (template: PracticeTemplateListItem) => {
    if (!user?.practice_id) return;
    
    try {
      const fullTemplate = await getPracticeTemplate(user.practice_id, template.id);
      setSelectedTemplate(fullTemplate);
      setShowModal(true);
    } catch (err: any) {
      alert('Failed to load template: ' + err.message);
    }
  };

  const handleSave = async (data: CreatePracticeTemplate) => {
    if (!user?.practice_id) return;

    if (selectedTemplate) {
      // Update existing template metadata
      await updatePracticeTemplate(user.practice_id, selectedTemplate.id, {
        name: data.name,
        species: data.species,
        description: data.description
      });
      
      // Delete existing items and add new ones
      for (const existingItem of selectedTemplate.items) {
        await deleteTemplateItem(user.practice_id, existingItem.id);
      }
      
      // Add new items
      if (data.items) {
        for (const item of data.items) {
          await addTemplateItem(user.practice_id, selectedTemplate.id, item);
        }
      }
    } else {
      await createPracticeTemplate(user.practice_id, data);
    }

    await fetchData();
  };

  const handleDelete = (template: PracticeTemplateListItem) => {
    setTemplateToDelete(template);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!templateToDelete || !user?.practice_id) return;

    try {
      await deletePracticeTemplate(user.practice_id, templateToDelete.id);
      await fetchData();
    } catch (err: any) {
      alert('Failed to delete template: ' + err.message);
    } finally {
      setShowDeleteConfirm(false);
      setTemplateToDelete(null);
    }
  };

  const toggleExpand = async (templateId: string) => {
    if (!user?.practice_id) return;

    if (expandedTemplate === templateId) {
      setExpandedTemplate(null);
      setExpandedDetails(null);
    } else {
      setExpandedTemplate(templateId);
      try {
        const details = await getPracticeTemplate(user.practice_id, templateId);
        setExpandedDetails(details);
      } catch (err) {
        console.error('Failed to load template details:', err);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-4 text-lg text-gray-600">Loading templates...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Header Actions */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4 justify-between items-center">
          <p className="text-gray-600">
            Create custom vaccination protocols or import and customize standard templates.
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowImportModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 bg-white rounded-md hover:bg-gray-50 transition-colors text-sm font-medium"
            >
              <Download className="w-4 h-4" />
              Import Template
            </button>
            <button
              onClick={handleCreate}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              Create Protocol
            </button>
          </div>
        </div>
      </div>

      {/* Templates List */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {templates.length === 0 ? (
          <div className="text-center py-16">
            <ClipboardList className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No protocols yet</h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              Import a standard template and customize it for your practice, or create one from scratch.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => setShowImportModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 bg-white rounded-md hover:bg-gray-50 transition-colors font-medium"
              >
                <Download className="w-4 h-4" />
                Import Template
              </button>
              <span className="text-gray-400">or</span>
              <button
                onClick={handleCreate}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
              >
                <Plus className="w-4 h-4" />
                Create from Scratch
              </button>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {templates.map((template) => (
              <div key={template.id}>
                <div className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => toggleExpand(template.id)}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                      >
                        {expandedTemplate === template.id ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </button>
                      <div>
                        <h3 className="font-medium text-gray-900">{template.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          {template.species && (
                            <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                              {template.species}
                            </span>
                          )}
                          <span className="text-xs text-gray-500">
                            {template.item_count} vaccine{template.item_count !== 1 ? 's' : ''}
                          </span>
                          {!template.is_active && (
                            <span className="text-xs px-2 py-0.5 bg-red-100 text-red-600 rounded">
                              Inactive
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setReminderTemplate(template);
                          setShowReminderModal(true);
                        }}
                        className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-md transition-colors"
                        title="Reminder Settings"
                      >
                        <Bell className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(template)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(template)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Expanded Details */}
                {expandedTemplate === template.id && expandedDetails && (
                  <div className="px-4 pb-4 bg-gray-50 border-t border-gray-100">
                    <div className="mt-3 ml-9">
                      {template.description && (
                        <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                      )}
                      <div className="space-y-2">
                        {expandedDetails.items.map((item, idx) => (
                          <div key={item.id} className="flex items-center gap-3 text-sm">
                            <span className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-xs font-medium">
                              {idx + 1}
                            </span>
                            <span className="font-medium">{item.immunization_name}</span>
                            <span className="text-gray-500">
                              {item.age_weeks != null ? (
                                `@ ${item.age_weeks} weeks old`
                              ) : item.age_days != null ? (
                                `@ ${item.age_days} days old`
                              ) : item.offset_days != null ? (
                                `Day ${item.offset_days}`
                              ) : (
                                'No schedule'
                              )}
                            </span>
                            {item.series_number && (
                              <span className="text-gray-400">
                                (Dose #{item.series_number})
                              </span>
                            )}
                            {item.catalog_item_name && (
                              <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded">
                                Linked to catalog
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <TemplateModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedTemplate(undefined);
        }}
        onSave={handleSave}
        template={selectedTemplate}
        catalogItems={catalogItems}
      />

      <ImportTemplateModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleImport}
        standardTemplates={standardTemplates}
      />

      {showDeleteConfirm && templateToDelete && (
        <ConfirmDialog
          isOpen={showDeleteConfirm}
          title="Delete Protocol"
          message={`Are you sure you want to delete "${templateToDelete.name}"? This action cannot be undone.`}
          confirmText="Delete"
          onConfirm={confirmDelete}
          onClose={() => {
            setShowDeleteConfirm(false);
            setTemplateToDelete(null);
          }}
        />
      )}

      <ReminderTriggersModal
        isOpen={showReminderModal}
        onClose={() => {
          setShowReminderModal(false);
          setReminderTemplate(null);
        }}
        template={reminderTemplate}
      />
    </div>
  );
};

export default PracticeTemplatesManagement;

